import { useSyncExternalStore } from "react";

/**
 * 에피소드 라벨링 모드. 사용자 단위 전역 선호이고, 리뷰 독의 최소화/최대화를 정한다.
 *
 * `view`(최소화)는 제외 줄 하나만 남기고 세로 공간을 영상에 준다. `review`(최대화)는 판정 축을
 * 모두 펼치고 단축키를 켠 채 에피소드를 연달아 판정한다. 접힘과 모드를 따로 두지 않는다. 같은
 * 것을 켜고 끄는 스위치가 둘이면 어느 쪽이 적용됐는지 알 수 없다.
 *
 * 데이터셋별 토큰이 아니라 **전역 값**이다. 그래서 창을 여러 개 띄워도, 리뷰·분석 탭을 바꿔도
 * 같은 모드를 쓴다. "나는 지금 판정 중이다"는 화면이 아니라 사용자의 상태이기 때문이다.
 *
 * 값을 localStorage 에 저장해 새로고침·새 탭에서도 유지한다. 저장소 접근이 막힌 환경
 * (사파리 private, storage 차단)에서는 메모리 값만 쓰고 조용히 degrade 한다.
 */

export type EpisodeReviewMode = "review" | "view";

const STORAGE_KEY = "hatchery-registry:episode-review-mode";

// 첫 방문 기본값. 최소화로 연다. 판정을 시작하기 전까지는 영상이 공간을 차지한다. 리뷰 모드는
// 사용자가 M·스위처로 직접 켠다.
const DEFAULT_MODE: EpisodeReviewMode = "view";

const listeners = new Set<() => void>();
let mode = readStored();

export const episodeReviewModeStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  mode(): EpisodeReviewMode {
    return mode;
  },
  set(next: EpisodeReviewMode): void {
    if (mode === next) {
      return;
    }
    mode = next;
    writeStored(next);
    for (const listener of listeners) {
      listener();
    }
  },
  toggle(): void {
    episodeReviewModeStore.set(mode === "review" ? "view" : "review");
  },
};

/** 에피소드 라벨링 모드. 독 머리의 스위처와 독 본문이 같은 값을 구독한다. */
export function useEpisodeReviewMode(): EpisodeReviewMode {
  return useSyncExternalStore(episodeReviewModeStore.subscribe, episodeReviewModeStore.mode);
}

function readStored(): EpisodeReviewMode {
  if (typeof window === "undefined") {
    return DEFAULT_MODE;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === "review" || raw === "view" ? raw : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

function writeStored(next: EpisodeReviewMode): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // 저장이 실패해도 이번 세션의 메모리 값만 잃는다. 기능을 막지 않는다.
  }
}
